/**
 * Days Service - Daily itinerary management with accommodation integration
 * 
 * This service manages day entries and their integration with accommodations,
 * providing methods for adding, removing, and managing accommodation content in days.
 */

import {
  getDays,
  getDay,
  addDay,
  updateDay,
  deleteDay,
  updateTrip,
  eventBridgeService,
  EVENT_TYPES,
  removeDocumentReferenceBlocks,
  updateDocumentReferenceBlocks
} from '@trip-planner-monorepo/shared';

import {
  extractAccommodationReferences,
  accommodationContentExists,
  removeAccommodationContentFromDay,
  calculateTripDatesFromDays
} from '../utils/accommodationDayUtils';

// Import new content block utilities
import {
  validateContentBlock,
  createContentBlock,
  updateContentBlock as updateContentBlockUtil,
  isStructuredContent,
  findContentBlockById,
  removeContentBlockById,
  getContentBlocksByType
} from '../utils/contentBlockUtils';

import { validateDayContentSchema } from '../schemas/contentBlock.schema';

export const daysService = {
  /**
   * Adds a number of days to an ISO date string using UTC-safe arithmetic.
   * This avoids DST/local timezone shifts that can cause off-by-one errors
   * when using Date with local time.
   * @param {string} isoDate YYYY-MM-DD
   * @param {number} daysOffset Positive/negative day offset
   * @returns {string} ISO date (YYYY-MM-DD)
   */
  _addDaysIsoUtc(isoDate, daysOffset) {
    if (!isoDate) return isoDate;
    try {
      const [y, m, d] = isoDate.split('-').map(Number);
      const utc = new Date(Date.UTC(y, (m - 1), d));
      utc.setUTCDate(utc.getUTCDate() + daysOffset);
      return utc.toISOString().split('T')[0];
    } catch (e) {
      console.warn('⚠️  [DaysService] _addDaysIsoUtc failed, returning original', { isoDate, daysOffset, error: e?.message });
      return isoDate;
    }
  },
  async fetchDays(userId, tripId) {
    try {
      return await getDays(userId, tripId);
    } catch (error) {
      console.error('Error fetching days:', error);
      throw error;
    }
  },

  async createDay(userId, tripId, dayData) {
    try {
      const days = await this.fetchDays(userId, tripId);
      if (!this.validateDaysLimit(days.length, 1)) {
        throw new Error('Maximum trip duration is 60 days');
      }

      const dayId = await addDay(userId, tripId, {
        ...dayData,
        order: days.length
      });

      // Update trip dates after creating day
      await this._updateTripDatesFromDays(userId, tripId);

      return dayId;
    } catch (error) {
      console.error('Error creating day:', error);
      throw error;
    }
  },

  async createMultipleDays(userId, tripId, daysData) {
    try {
      const existingDays = await this.fetchDays(userId, tripId);
      if (!this.validateDaysLimit(existingDays.length, daysData.length)) {
        throw new Error('Maximum trip duration is 60 days');
      }

      for (const dayData of daysData) {
        await addDay(userId, tripId, dayData);
      }

      // Update trip dates after creating multiple days
      await this._updateTripDatesFromDays(userId, tripId);

      return await this.fetchDays(userId, tripId);
    } catch (error) {
      console.error('Error creating multiple days:', error);
      throw error;
    }
  },

  async updateDayAndAdjustSequence(userId, tripId, dayId, updatedDay, allDays) {
    console.log('📅 [DaysService] updateDayAndAdjustSequence: Starting update with Phase 2B content blocks', {
      userId,
      tripId,
      dayId,
      hasContent: !!updatedDay.content,
      contentType: typeof updatedDay.content,
      allDaysCount: allDays.length
    });

    try {
      const dayIndex = allDays.findIndex(d => d.id === dayId);
      if (dayIndex === -1) throw new Error('Day not found');

      const updatedDays = [...allDays];

      // Process the updated day content to ensure it's in structured format
      let processedUpdatedDay = { ...updatedDay, id: dayId };

      // Process structured content blocks
      if (updatedDay.content !== undefined) {
        console.log('🔄 [DaysService] updateDayAndAdjustSequence: Processing structured content', {
          dayId,
          contentType: typeof updatedDay.content,
          isArray: Array.isArray(updatedDay.content),
          blockCount: Array.isArray(updatedDay.content) ? updatedDay.content.length : 0
        });

        if (Array.isArray(updatedDay.content)) {
          // Structured content - validate it
          const contentValidation = validateDayContentSchema(updatedDay.content);
          if (!contentValidation.isValid) {
            console.error('❌ [DaysService] updateDayAndAdjustSequence: Invalid structured content', {
              dayId,
              errors: contentValidation.errors
            });
            throw new Error(`Invalid day content: ${contentValidation.errors.join(', ')}`);
          }
          processedUpdatedDay.content = updatedDay.content;
          console.log('✅ [DaysService] updateDayAndAdjustSequence: Structured content validated', {
            dayId,
            blockCount: updatedDay.content.length,
            blockTypes: updatedDay.content.map(block => block.type)
          });
        } else {
          // Unknown content type - use empty array
          console.warn('⚠️ [DaysService] updateDayAndAdjustSequence: Non-array content, using empty array', {
            dayId,
            contentType: typeof updatedDay.content
          });
          processedUpdatedDay.content = [];
        }
      }

      updatedDays[dayIndex] = processedUpdatedDay;

      if (updatedDay.date) {
        const editedIso = updatedDay.date;
        console.log('📆 [DaysService] Date recalculation starting', {
          editedDayIndex: dayIndex,
          editedDate: editedIso,
          totalDays: updatedDays.length
        });

        // Update previous days
        for (let i = dayIndex - 1; i >= 0; i--) {
          const daysOffset = dayIndex - i;
          const calculatedDate = this._addDaysIsoUtc(editedIso, -daysOffset);
          updatedDays[i] = {
            ...updatedDays[i],
            date: calculatedDate
          };
          console.log(`  ⬅️ Day ${i + 1} (index ${i}): ${calculatedDate} (base date - ${daysOffset})`);
        }

        // Update following days
        for (let i = dayIndex + 1; i < updatedDays.length; i++) {
          const daysOffset = i - dayIndex;
          const calculatedDate = this._addDaysIsoUtc(editedIso, daysOffset);
          updatedDays[i] = {
            ...updatedDays[i],
            date: calculatedDate
          };
          console.log(`  ➡️ Day ${i + 1} (index ${i}): ${calculatedDate} (base date + ${daysOffset})`);
        }

        console.log('✅ [DaysService] Date recalculation complete');
      }

      // Update all days in Firestore
      const updatePromises = updatedDays.map(day => {
        const updateData = {
          date: day.date,
          order: day.order
        };

        // Only include content if it exists (avoid undefined values)
        if (day.content !== undefined) {
          updateData.content = day.content;
        }

        console.log(`💾 [DaysService] updateDayAndAdjustSequence: Updating day ${day.id}`, {
          hasContent: !!updateData.content,
          contentType: Array.isArray(updateData.content) ? 'structured' : typeof updateData.content,
          contentBlockCount: Array.isArray(updateData.content) ? updateData.content.length : 'N/A'
        });

        return updateDay(userId, tripId, day.id, updateData);
      });

      await Promise.all(updatePromises);

      console.log('✅ [DaysService] updateDayAndAdjustSequence: All days updated successfully');

      // Update trip dates after day date modification
      await this._updateTripDatesFromDays(userId, tripId);

      return await this.fetchDays(userId, tripId);
    } catch (error) {
      console.error('❌ [DaysService] updateDayAndAdjustSequence: Update failed', {
        userId,
        tripId,
        dayId,
        error: error.message,
        stack: error.stack
      });
      throw error;
    }
  },

  async deleteDay(userId, tripId, dayId) {
    try {
      // Get current days before deletion
      const days = await this.fetchDays(userId, tripId);
      const dayIndex = days.findIndex(d => d.id === dayId);
      if (dayIndex === -1) throw new Error('Day not found');
      const dayToDelete = days[dayIndex];

      // Delete the day
      await deleteDay(userId, tripId, dayId);

      // Get remaining days
      const remainingDays = days.filter(d => d.id !== dayId);

      // If there are remaining days with dates, recalculate sequence to keep days sequential (UTC-safe)
      if (remainingDays.length > 0 && remainingDays[0].date) {
        const baseIso = remainingDays[0].date; // YYYY-MM-DD

        const updatePromises = remainingDays.map((day, index) => {
          return updateDay(userId, tripId, day.id, {
            ...day,
            date: this._addDaysIsoUtc(baseIso, index),
            order: index
          });
        });

        await Promise.all(updatePromises);
      } else {
        // If no dates, just update orders
        const updatePromises = remainingDays.map((day, index) =>
          updateDay(userId, tripId, day.id, {
            ...day,
            order: index
          })
        );
        await Promise.all(updatePromises);
      }

      // Update trip dates after deleting day
      await this._updateTripDatesFromDays(userId, tripId);

      eventBridgeService.publish(EVENT_TYPES.DAY_DELETED, {
        userId,
        tripId,
        id: dayId,
        dayId,
        date: dayToDelete.date || null,
        title: dayToDelete.title || null,
        timestamp: new Date().toISOString()
      });

      return await this.fetchDays(userId, tripId);
    } catch (error) {
      console.error('Error deleting day:', error);
      throw error;
    }
  },

  async reorderDays(userId, tripId, reorderedDays) {
    try {
      // Update orders for all days
      const updatePromises = reorderedDays.map((day, index) =>
        updateDay(userId, tripId, day.id, {
          ...day,
          order: index
        })
      );

      await Promise.all(updatePromises);
      return await this.fetchDays(userId, tripId);
    } catch (error) {
      console.error('Error reordering days:', error);
      throw error;
    }
  },

  validateDaysLimit(currentDays, daysToAdd) {
    return (currentDays + daysToAdd) <= 60;
  },

  /**
   * Recalculates and updates trip dates based on current days
   * Called internally after any day operation that might affect trip dates
   * @param {string} userId - User ID
   * @param {string} tripId - Trip ID
   * @private
   */
  async _updateTripDatesFromDays(userId, tripId) {
    console.log('🔄 [DaysService] _updateTripDatesFromDays called', { userId, tripId });
    try {
      // Fetch all current days for the trip
      const days = await this.fetchDays(userId, tripId);
      console.log('📅 [DaysService] Fetched days for trip date calculation:', {
        dayCount: days.length,
        daysWithDates: days.filter(d => d.date).length
      });

      // Calculate new date range using the utility function
      const { startDate, endDate } = calculateTripDatesFromDays(days);
      console.log('📊 [DaysService] Calculated dates from days:', { startDate, endDate });

      // Update trip in Firestore with calculated dates
      await updateTrip(userId, tripId, {
        startDate,
        endDate,
        updatedAt: new Date().toISOString()
      });

      console.log(`✅ [DaysService] Updated trip dates in Firestore: ${startDate || 'null'} to ${endDate || 'null'}`);

      // Publish event to notify other parts of the app (e.g., TripContext to refresh banner)
      console.log('📢 [DaysService] Publishing TRIP_UPDATED event');
      eventBridgeService.publish(EVENT_TYPES.TRIP_UPDATED, {
        userId,
        tripId,
        startDate,
        endDate,
        timestamp: new Date().toISOString()
      });
      console.log('✅ [DaysService] TRIP_UPDATED event published successfully');
    } catch (error) {
      console.error('❌ [DaysService] Error updating trip dates from days:', error);
      console.error('❌ [DaysService] Error stack:', error.stack);
      // Don't throw - this is a secondary operation, shouldn't break primary day operations
    }
  },

  // Accommodation integration methods (existing functionality)
  async getAccommodationReferencesInDay(userId, tripId, dayId) {
    try {
      const day = await getDay(userId, tripId, dayId);
      if (!day || !day.content) {
        return [];
      }

      return extractAccommodationReferences(day.content);
    } catch (error) {
      console.error('Error getting accommodation references in day:', error);
      return [];
    }
  },

  async addAccommodationToDay(userId, tripId, dayId, accommodationContent, accommodationId) {
    try {
      // Check if day exists
      let day = await getDay(userId, tripId, dayId);

      if (day) {
        // Check for duplicates - simple string matching for exact content
        if (day.content && day.content.includes(accommodationContent)) {
          return {
            success: true,
            action: 'duplicate_skipped',
            dayId: day.id
          };
        }

        // Add content to existing day
        const updatedContent = day.content ? `${day.content}\n${accommodationContent}` : accommodationContent;

        await updateDay(userId, tripId, day.id, { content: updatedContent });

        // Publish event
        eventBridgeService.publish(EVENT_TYPES.DAY_ACCOMMODATION_ADDED, {
          userId,
          tripId,
          dayId: day.id,
          accommodationId,
          content: accommodationContent,
          timestamp: new Date().toISOString(),
          eventId: `evt_${Date.now()}`
        });

        return {
          success: true,
          action: 'updated',
          dayId: day.id
        };
      } else {
        // Day doesn't exist - this shouldn't happen with dayId parameter
        // Return error since we expect day to exist when using dayId
        return {
          success: false,
          error: 'Day not found with the specified ID'
        };
      }
    } catch (error) {
      console.error('Error adding accommodation to day:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  async removeAccommodationFromDay(userId, tripId, dayId, accommodationId) {
    try {
      const day = await getDay(userId, tripId, dayId);

      if (!day) {
        return {
          success: true,
          action: 'day_not_found'
        };
      }

      // Extract accommodation references to find the one to remove
      const references = extractAccommodationReferences(day.content);
      const accommodationRef = references.find(ref => ref.accommodationId === accommodationId);

      if (!accommodationRef) {
        return {
          success: true,
          action: 'not_found'
        };
      }

      const result = removeAccommodationContentFromDay(day.content, accommodationRef.accommodationName);

      if (!result.removed) {
        return {
          success: true,
          action: 'not_found'
        };
      }

      // Update day with removed content
      await updateDay(userId, tripId, day.id, { content: result.content });

      // Publish event
      eventBridgeService.publish(EVENT_TYPES.DAY_ACCOMMODATION_REMOVED, {
        userId,
        tripId,
        dayId: day.id,
        accommodationId,
        accommodationName: accommodationRef.accommodationName,
        removedContent: result.removedLine,
        timestamp: new Date().toISOString(),
        eventId: `evt_${Date.now()}`
      });

      return {
        success: true,
        action: 'removed',
        dayId: day.id,
        removedContent: result.removedLine
      };
    } catch (error) {
      console.error('Error removing accommodation from day:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Helper methods (now using actual Firestore operations)
  async getDaysByDateRange(userId, tripId, startDate, endDate) {
    try {
      const allDays = await getDays(userId, tripId);
      if (!startDate || !endDate) return allDays;

      return allDays.filter(day => {
        if (!day.date) return false;
        const dayDate = new Date(day.date);
        return dayDate >= new Date(startDate) && dayDate <= new Date(endDate);
      });
    } catch (error) {
      console.error('Error getting days by date range:', error);
      throw error;
    }
  },

  async getDayByDate(userId, tripId, date) {
    console.log("=== DAYS SERVICE GET DAY BY DATE START ===");
    console.log("getDayByDate called with:");
    console.log("  - userId:", userId);
    console.log("  - tripId:", tripId);
    console.log("  - date:", date);
    console.log("  - date type:", typeof date);

    try {
      console.log("getDayByDate: STEP 1 - Calling getDays to fetch all days");
      const allDays = await getDays(userId, tripId);
      console.log("getDayByDate: STEP 1 RESULT - Retrieved", allDays ? allDays.length : 0, "days");
      console.log("getDayByDate: STEP 1 RESULT - All days:", JSON.stringify(allDays, null, 2));

      console.log("getDayByDate: STEP 2 - Searching for day with matching date");
      console.log("getDayByDate: STEP 2 - Target date:", date);

      if (!allDays || allDays.length === 0) {
        console.log("getDayByDate: STEP 2 - No days found in collection");
        console.log("=== DAYS SERVICE GET DAY BY DATE END (NO DAYS) ===");
        return null;
      }

      let matchingDay = null;
      for (let i = 0; i < allDays.length; i++) {
        const day = allDays[i];
        console.log(`getDayByDate: STEP 2.${i + 1} - Checking day ${i + 1}:`);
        console.log(`  - day.date: "${day.date}" (type: ${typeof day.date})`);
        console.log(`  - target date: "${date}" (type: ${typeof date})`);
        console.log(`  - exact match: ${day.date === date}`);
        console.log(`  - day object:`, JSON.stringify(day, null, 2));

        if (day.date === date) {
          console.log(`getDayByDate: STEP 2.${i + 1} - MATCH FOUND!`);
          matchingDay = day;
          break;
        }
      }

      if (matchingDay) {
        console.log("getDayByDate: STEP 2 SUCCESS - Found matching day:", JSON.stringify(matchingDay, null, 2));
        console.log("=== DAYS SERVICE GET DAY BY DATE END (FOUND) ===");
        return matchingDay;
      } else {
        console.log("getDayByDate: STEP 2 - No matching day found");
        console.log("=== DAYS SERVICE GET DAY BY DATE END (NOT FOUND) ===");
        return null;
      }
    } catch (error) {
      console.error('getDayByDate: ERROR CAUGHT - Error getting day by date:', error);
      console.error('getDayByDate: ERROR STACK:', error.stack);
      console.log("=== DAYS SERVICE GET DAY BY DATE END (ERROR) ===");
      throw error;
    }
  },

  // Date-based accommodation integration (for accommodation date ranges)
  async addAccommodationToDateIfDayExists(userId, tripId, date, accommodationContent, accommodationId) {
    try {
      const day = await this.getDayByDate(userId, tripId, date);

      if (!day) {
        return {
          success: false,
          action: 'day_not_found_for_date',
          date: date
        };
      }

      // Use the dayId-based method now that we found the day
      return await this.addAccommodationToDay(userId, tripId, day.id, accommodationContent, accommodationId);
    } catch (error) {
      console.error('Error adding accommodation to date:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  async removeAccommodationFromDateIfDayExists(userId, tripId, date, accommodationId) {
    try {
      const day = await this.getDayByDate(userId, tripId, date);

      if (!day) {
        return {
          success: true,
          action: 'day_not_found_for_date',
          date: date
        };
      }

      // Use the dayId-based method now that we found the day
      return await this.removeAccommodationFromDay(userId, tripId, day.id, accommodationId);
    } catch (error) {
      console.error('Error removing accommodation from date:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  formatDateTitle(date) {
    const dateObj = new Date(date);
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  },

  // ===== NEW CONTENT BLOCK METHODS (Phase 2B) =====

  /**
   * Adds a content block to a day's content array
   * @param {string} userId - User ID
   * @param {string} tripId - Trip ID
   * @param {string} dayId - Day ID
   * @param {Object} contentBlock - Content block to add
   * @returns {Object} Operation result with success status
   */
  async addContentBlock(userId, tripId, dayId, contentBlock) {
    console.log('📅 [DaysService] addContentBlock: Starting operation', {
      userId,
      tripId,
      dayId,
      contentBlockType: contentBlock?.type,
      contentBlockId: contentBlock?.id
    });

    try {
      // Validate content block
      const validation = validateContentBlock(contentBlock);
      if (!validation.isValid) {
        console.error('❌ [DaysService] addContentBlock: Content block validation failed', {
          dayId,
          contentBlockType: contentBlock?.type,
          errors: validation.errors
        });
        throw new Error(`Invalid content block: ${validation.errors.join(', ')}`);
      }

      console.log('✅ [DaysService] addContentBlock: Content block validation passed');

      // Get current day
      const day = await getDay(userId, tripId, dayId);
      if (!day) {
        console.error('❌ [DaysService] addContentBlock: Day not found', {
          userId,
          tripId,
          dayId
        });
        throw new Error('Day not found');
      }

      console.log('📋 [DaysService] addContentBlock: Day retrieved successfully', {
        dayId,
        hasContent: !!day.content,
        contentType: typeof day.content
      });

      // Get current content and ensure it's in structured format
      let currentContent = await this.getDayContent(userId, tripId, dayId);

      console.log('📊 [DaysService] addContentBlock: Current content retrieved', {
        dayId,
        currentContentLength: currentContent.length,
        isStructured: Array.isArray(currentContent),
        contentBlockTypes: currentContent.map(block => block.type)
      });

      // Add the new content block
      const updatedContent = [...currentContent, contentBlock];

      console.log('🔧 [DaysService] addContentBlock: Content block added to array', {
        dayId,
        newContentLength: updatedContent.length,
        addedBlockType: contentBlock.type,
        addedBlockId: contentBlock.id
      });

      // Validate the updated content array
      const contentValidation = validateDayContentSchema(updatedContent);
      if (!contentValidation.isValid) {
        console.error('❌ [DaysService] addContentBlock: Day content validation failed', {
          dayId,
          contentLength: updatedContent.length,
          errors: contentValidation.errors
        });
        throw new Error(`Invalid day content: ${contentValidation.errors.join(', ')}`);
      }

      console.log('✅ [DaysService] addContentBlock: Day content validation passed');

      // Update the day
      await updateDay(userId, tripId, dayId, { content: updatedContent });

      console.log('💾 [DaysService] addContentBlock: Day updated in database');

      // Publish event
      eventBridgeService.publish(EVENT_TYPES.DAY_CONTENT_BLOCK_ADDED, {
        userId,
        tripId,
        dayId,
        contentBlock,
        timestamp: new Date().toISOString(),
        eventId: `evt_${Date.now()}`
      });

      console.log('📡 [DaysService] addContentBlock: Event published', {
        eventType: EVENT_TYPES.DAY_CONTENT_BLOCK_ADDED,
        dayId,
        contentBlockId: contentBlock.id
      });

      const result = {
        success: true,
        contentBlock,
        dayId
      };

      console.log('✅ [DaysService] addContentBlock: Operation completed successfully', result);

      return result;
    } catch (error) {
      console.error('❌ [DaysService] addContentBlock: Operation failed', {
        userId,
        tripId,
        dayId,
        contentBlockType: contentBlock?.type,
        contentBlockId: contentBlock?.id,
        error: error.message,
        stack: error.stack
      });
      throw error;
    }
  },

  /**
   * Removes a content block from a day by its ID
   * @param {string} userId - User ID
   * @param {string} tripId - Trip ID
   * @param {string} dayId - Day ID
   * @param {string} contentBlockId - ID of content block to remove
   * @returns {Object} Operation result with success status
   */
  async removeContentBlock(userId, tripId, dayId, contentBlockId) {
    try {
      // Get current day
      const day = await getDay(userId, tripId, dayId);
      if (!day) {
        throw new Error('Day not found');
      }

      // Get current content and ensure it's in structured format
      let currentContent = await this.getDayContent(userId, tripId, dayId);

      // Find the content block to remove
      const blockToRemove = findContentBlockById(currentContent, contentBlockId);
      if (!blockToRemove) {
        return {
          success: true,
          action: 'not_found',
          contentBlockId
        };
      }

      // Remove the content block
      const updatedContent = removeContentBlockById(currentContent, contentBlockId);

      // Update the day
      await updateDay(userId, tripId, dayId, { content: updatedContent });

      // Publish event
      eventBridgeService.publish(EVENT_TYPES.DAY_CONTENT_BLOCK_REMOVED, {
        userId,
        tripId,
        dayId,
        contentBlockId,
        removedBlock: blockToRemove,
        timestamp: new Date().toISOString(),
        eventId: `evt_${Date.now()}`
      });

      return {
        success: true,
        action: 'removed',
        contentBlockId,
        removedBlock: blockToRemove
      };
    } catch (error) {
      console.error('Error removing content block:', error);
      throw error;
    }
  },

  /**
   * Updates an existing content block
   * @param {string} userId - User ID
   * @param {string} tripId - Trip ID
   * @param {string} dayId - Day ID
   * @param {string} contentBlockId - ID of content block to update
   * @param {Object} updates - Updates to apply
   * @returns {Object} Operation result with success status
   */
  async updateContentBlock(userId, tripId, dayId, contentBlockId, updates) {
    try {
      // Get current day
      const day = await getDay(userId, tripId, dayId);
      if (!day) {
        throw new Error('Day not found');
      }

      // Get current content and ensure it's in structured format
      let currentContent = await this.getDayContent(userId, tripId, dayId);

      // Find the content block to update
      const blockIndex = currentContent.findIndex(block => block.id === contentBlockId);
      if (blockIndex === -1) {
        throw new Error('Content block not found');
      }

      // Update the content block
      const existingBlock = currentContent[blockIndex];
      const updatedBlock = updateContentBlockUtil(existingBlock, updates);

      // Create updated content array
      const updatedContent = [...currentContent];
      updatedContent[blockIndex] = updatedBlock;

      // Validate the updated content array
      const contentValidation = validateDayContentSchema(updatedContent);
      if (!contentValidation.isValid) {
        throw new Error(`Invalid day content: ${contentValidation.errors.join(', ')}`);
      }

      // Update the day
      await updateDay(userId, tripId, dayId, { content: updatedContent });

      // Publish event
      eventBridgeService.publish(EVENT_TYPES.DAY_CONTENT_BLOCK_UPDATED, {
        userId,
        tripId,
        dayId,
        contentBlockId,
        oldBlock: existingBlock,
        updatedBlock,
        timestamp: new Date().toISOString(),
        eventId: `evt_${Date.now()}`
      });

      return {
        success: true,
        contentBlock: updatedBlock,
        dayId
      };
    } catch (error) {
      console.error('Error updating content block:', error);
      throw error;
    }
  },

  /**
   * Gets content blocks from a day, optionally filtered by type
   * @param {string} userId - User ID
   * @param {string} tripId - Trip ID
   * @param {string} dayId - Day ID
   * @param {string} type - Optional type filter
   * @returns {Array} Array of content blocks
   */
  async getContentBlocks(userId, tripId, dayId, type = null) {
    try {
      // Get current content and ensure it's in structured format
      const currentContent = await this.getDayContent(userId, tripId, dayId);

      if (type) {
        return getContentBlocksByType(currentContent, type);
      }

      return currentContent;
    } catch (error) {
      console.error('Error getting content blocks:', error);
      throw error;
    }
  },

  /**
   * Gets day content in structured format, converting from legacy if needed
   * @param {string} userId - User ID
   * @param {string} tripId - Trip ID
   * @param {string} dayId - Day ID
   * @returns {Array} Array of structured content blocks
   */
  async getDayContent(userId, tripId, dayId) {
    console.log('📖 [DaysService] getDayContent: Starting content retrieval', {
      userId,
      tripId,
      dayId
    });

    try {
      const day = await getDay(userId, tripId, dayId);
      if (!day) {
        console.warn('⚠️ [DaysService] getDayContent: Day not found, returning empty array', {
          userId,
          tripId,
          dayId
        });
        return [];
      }

      // Support both contentBlocks and content fields for backward compatibility
      const contentData = day.contentBlocks || day.content;

      console.log('📋 [DaysService] getDayContent: Day retrieved', {
        dayId,
        hasContentBlocks: !!day.contentBlocks,
        hasContent: !!day.content,
        contentType: typeof contentData,
        isArray: Array.isArray(contentData),
        contentPreview: typeof contentData === 'string' ? contentData.substring(0, 100) : 'N/A'
      });

      // Check content format
      if (isStructuredContent(contentData)) {
        console.log('✅ [DaysService] getDayContent: Content is structured', {
          dayId,
          contentBlockCount: contentData.length,
          blockTypes: contentData.map(block => block.type)
        });
        return contentData;
      }

      // No content or non-structured format - return empty array
      console.log('📝 [DaysService] getDayContent: No structured content found, returning empty array', {
        dayId,
        contentType: typeof day.content
      });
      return [];
    } catch (error) {
      console.error('❌ [DaysService] getDayContent: Operation failed', {
        userId,
        tripId,
        dayId,
        error: error.message,
        stack: error.stack
      });
      throw error;
    }
  },

  /**
   * Updates day content with structured content blocks
   * @param {string} userId - User ID
   * @param {string} tripId - Trip ID
   * @param {string} dayId - Day ID
   * @param {Array} contentBlocks - Array of content blocks
   * @returns {Object} Operation result
   */
  async updateDayContent(userId, tripId, dayId, contentBlocks) {
    try {
      // Validate the content array
      const contentValidation = validateDayContentSchema(contentBlocks);
      if (!contentValidation.isValid) {
        throw new Error(`Invalid day content: ${contentValidation.errors.join(', ')}`);
      }

      // Update the day
      await updateDay(userId, tripId, dayId, { content: contentBlocks });

      return {
        success: true,
        contentBlocks,
        dayId
      };
    } catch (error) {
      console.error('Error updating day content:', error);
      throw error;
    }
  },

  /**
   * Converts legacy text content to structured format for a specific day
   * @param {string} userId - User ID
   * @param {string} tripId - Trip ID
   * @param {string} dayId - Day ID
   * @returns {Object} Conversion result
   */
  async getStructuredContent(userId, tripId, dayId) {
    try {
      const day = await getDay(userId, tripId, dayId);
      if (!day) {
        throw new Error('Day not found');
      }

      if (isStructuredContent(day.content)) {
        return {
          success: true,
          action: 'structured_content_found',
          contentBlocks: day.content
        };
      }

      return {
        success: true,
        action: 'no_structured_content',
        contentBlocks: []
      };
    } catch (error) {
      console.error('Error retrieving structured content:', error);
      throw error;
    }
  },

  /**
   * Checks if day content is in structured format
   * @param {string} userId - User ID
   * @param {string} tripId - Trip ID
   * @param {string} dayId - Day ID
   * @returns {boolean} True if content is structured
   */
  async isContentStructured(userId, tripId, dayId) {
    try {
      const day = await getDay(userId, tripId, dayId);
      if (!day) {
        return false;
      }

      return isStructuredContent(day.content);
    } catch (error) {
      console.error('Error checking content structure:', error);
      return false;
    }
  },

  /**
   * Adds an accommodation content block to the end of a day's content
   * This ensures accommodation entries ("Stay at...") are always the last line
   * @param {string} userId - User ID
   * @param {string} tripId - Trip ID
   * @param {string} dayId - Day ID
   * @param {Object} accommodationContentBlock - Accommodation content block to add
   * @returns {Object} Operation result with success status
   */
  async addAccommodationContentBlock(userId, tripId, dayId, accommodationContentBlock) {
    console.log('🏨 [DaysService] addAccommodationContentBlock: Starting operation', {
      userId,
      tripId,
      dayId,
      contentBlockId: accommodationContentBlock?.id,
      accommodationId: accommodationContentBlock?.accommodationId
    });

    try {
      // Validate that this is actually an accommodation content block
      if (accommodationContentBlock.type !== 'accommodation_reference') {
        console.error('❌ [DaysService] addAccommodationContentBlock: Not an accommodation content block', {
          dayId,
          contentBlockType: accommodationContentBlock.type
        });
        throw new Error('Content block must be of type accommodation_reference');
      }

      // Validate content block
      const validation = validateContentBlock(accommodationContentBlock);
      if (!validation.isValid) {
        console.error('❌ [DaysService] addAccommodationContentBlock: Content block validation failed', {
          dayId,
          contentBlockId: accommodationContentBlock.id,
          errors: validation.errors
        });
        throw new Error(`Invalid accommodation content block: ${validation.errors.join(', ')}`);
      }

      console.log('✅ [DaysService] addAccommodationContentBlock: Content block validation passed');

      // Get current day
      const day = await getDay(userId, tripId, dayId);
      if (!day) {
        console.error('❌ [DaysService] addAccommodationContentBlock: Day not found', {
          userId,
          tripId,
          dayId
        });
        throw new Error('Day not found');
      }

      console.log('📋 [DaysService] addAccommodationContentBlock: Day retrieved successfully');

      // Get current content and ensure it's in structured format
      let currentContent = await this.getDayContent(userId, tripId, dayId);

      console.log('📊 [DaysService] addAccommodationContentBlock: Current content retrieved', {
        dayId,
        currentContentLength: currentContent.length,
        contentBlockTypes: currentContent.map(block => block.type)
      });

      // Separate accommodation blocks from other content blocks
      const nonAccommodationBlocks = currentContent.filter(block => block.type !== 'accommodation_reference');
      const existingAccommodationBlocks = currentContent.filter(block => block.type === 'accommodation_reference');

      // Check if this accommodation is already referenced
      const existingAccommodationBlock = existingAccommodationBlocks.find(
        block => block.accommodationId === accommodationContentBlock.accommodationId
      );

      if (existingAccommodationBlock) {
        console.log('⚠️ [DaysService] addAccommodationContentBlock: Accommodation already referenced in this day', {
          dayId,
          accommodationId: accommodationContentBlock.accommodationId,
          existingBlockId: existingAccommodationBlock.id
        });
        // Update the existing block instead of adding a duplicate
        const blockUpdates = {
          updatedAt: new Date().toISOString()
        };
        if (accommodationContentBlock.displayText !== undefined) {
          blockUpdates.displayText = accommodationContentBlock.displayText;
        }
        if (accommodationContentBlock.context !== undefined) {
          blockUpdates.context = accommodationContentBlock.context;
        }
        if (accommodationContentBlock.accommodationName !== undefined) {
          blockUpdates.accommodationName = accommodationContentBlock.accommodationName;
        }
        if (accommodationContentBlock.placeName !== undefined) {
          blockUpdates.placeName = accommodationContentBlock.placeName;
        }
        return await this.updateContentBlock(userId, tripId, dayId, existingAccommodationBlock.id, blockUpdates);
      }

      // Additional check: Remove any duplicate blocks with the same placeName and accommodationName
      // This prevents duplicates from accumulating during date changes
      const duplicateBlocks = existingAccommodationBlocks.filter(
        block => block.placeName === accommodationContentBlock.placeName &&
          block.accommodationName === accommodationContentBlock.accommodationName &&
          block.accommodationId !== accommodationContentBlock.accommodationId
      );

      if (duplicateBlocks.length > 0) {
        console.log('🧹 [DaysService] addAccommodationContentBlock: Removing duplicate blocks with same place name', {
          dayId,
          placeName: accommodationContentBlock.placeName,
          accommodationName: accommodationContentBlock.accommodationName,
          duplicateBlocksCount: duplicateBlocks.length,
          duplicateBlockIds: duplicateBlocks.map(block => block.id)
        });

        // Remove duplicate blocks from the existing accommodation blocks
        const cleanedAccommodationBlocks = existingAccommodationBlocks.filter(
          block => !duplicateBlocks.includes(block)
        );

        // Update the existing accommodation blocks to the cleaned version
        const updatedContent = [
          ...nonAccommodationBlocks,
          ...cleanedAccommodationBlocks,
          accommodationContentBlock
        ];

        console.log('🔧 [DaysService] addAccommodationContentBlock: Content cleaned of duplicates', {
          dayId,
          removedBlocks: duplicateBlocks.length,
          finalAccommodationBlocks: cleanedAccommodationBlocks.length + 1
        });

        // Validate the updated content array
        const contentValidation = validateDayContentSchema(updatedContent);
        if (!contentValidation.isValid) {
          console.error('❌ [DaysService] addAccommodationContentBlock: Day content validation failed after cleanup', {
            dayId,
            contentLength: updatedContent.length,
            errors: contentValidation.errors
          });
          throw new Error(`Invalid day content after cleanup: ${contentValidation.errors.join(', ')}`);
        }

        console.log('✅ [DaysService] addAccommodationContentBlock: Day content validation passed after cleanup');

        // Update the day
        await updateDay(userId, tripId, dayId, { content: updatedContent });

        console.log('💾 [DaysService] addAccommodationContentBlock: Day updated in database after cleanup');

        // Publish event
        eventBridgeService.publish(EVENT_TYPES.DAY_CONTENT_BLOCK_ADDED, {
          userId,
          tripId,
          dayId,
          contentBlock: accommodationContentBlock,
          accommodationId: accommodationContentBlock.accommodationId,
          addedToEnd: true,
          duplicatesRemoved: duplicateBlocks.length,
          timestamp: new Date().toISOString(),
          eventId: `evt_${Date.now()}`
        });

        console.log('📡 [DaysService] addAccommodationContentBlock: Event published with cleanup info', {
          eventType: EVENT_TYPES.DAY_CONTENT_BLOCK_ADDED,
          dayId,
          accommodationId: accommodationContentBlock.accommodationId,
          contentBlockId: accommodationContentBlock.id,
          duplicatesRemoved: duplicateBlocks.length
        });

        const result = {
          success: true,
          contentBlock: accommodationContentBlock,
          dayId,
          addedToEnd: true,
          duplicatesRemoved: duplicateBlocks.length
        };

        console.log('✅ [DaysService] addAccommodationContentBlock: Operation completed successfully with cleanup', result);

        return result;
      }

      // Build the new content array: non-accommodation blocks first, then all accommodation blocks at the end
      const updatedContent = [
        ...nonAccommodationBlocks,
        ...existingAccommodationBlocks,
        accommodationContentBlock
      ];

      console.log('🔧 [DaysService] addAccommodationContentBlock: Content reorganized with accommodation at end', {
        dayId,
        newContentLength: updatedContent.length,
        accommodationBlocksCount: existingAccommodationBlocks.length + 1,
        addedBlockId: accommodationContentBlock.id,
        finalContentTypes: updatedContent.map(block => block.type)
      });

      // Validate the updated content array
      const contentValidation = validateDayContentSchema(updatedContent);
      if (!contentValidation.isValid) {
        console.error('❌ [DaysService] addAccommodationContentBlock: Day content validation failed', {
          dayId,
          contentLength: updatedContent.length,
          errors: contentValidation.errors
        });
        throw new Error(`Invalid day content: ${contentValidation.errors.join(', ')}`);
      }

      console.log('✅ [DaysService] addAccommodationContentBlock: Day content validation passed');

      // Update the day
      await updateDay(userId, tripId, dayId, { content: updatedContent });

      console.log('💾 [DaysService] addAccommodationContentBlock: Day updated in database');

      // Publish event
      eventBridgeService.publish(EVENT_TYPES.DAY_CONTENT_BLOCK_ADDED, {
        userId,
        tripId,
        dayId,
        contentBlock: accommodationContentBlock,
        accommodationId: accommodationContentBlock.accommodationId,
        addedToEnd: true,
        timestamp: new Date().toISOString(),
        eventId: `evt_${Date.now()}`
      });

      console.log('📡 [DaysService] addAccommodationContentBlock: Event published', {
        eventType: EVENT_TYPES.DAY_CONTENT_BLOCK_ADDED,
        dayId,
        accommodationId: accommodationContentBlock.accommodationId,
        contentBlockId: accommodationContentBlock.id
      });

      const result = {
        success: true,
        contentBlock: accommodationContentBlock,
        dayId,
        addedToEnd: true
      };

      console.log('✅ [DaysService] addAccommodationContentBlock: Operation completed successfully', result);

      return result;
    } catch (error) {
      console.error('❌ [DaysService] addAccommodationContentBlock: Operation failed', {
        userId,
        tripId,
        dayId,
        accommodationId: accommodationContentBlock?.accommodationId,
        contentBlockId: accommodationContentBlock?.id,
        error: error.message,
        stack: error.stack
      });
      throw error;
    }
  },

  /**
   * Removes all accommodation content blocks for a specific accommodation from a day
   * @param {string} userId - User ID
   * @param {string} tripId - Trip ID
   * @param {string} dayId - Day ID
   * @param {string} accommodationId - Accommodation ID to remove
   * @returns {Object} Operation result with success status and count of removed blocks
   */
  async removeAccommodationContentBlocks(userId, tripId, dayId, accommodationId) {
    console.log('🗑️ [DaysService] removeAccommodationContentBlocks: Starting operation', {
      userId,
      tripId,
      dayId,
      accommodationId
    });

    try {
      // Get current day
      const day = await getDay(userId, tripId, dayId);
      if (!day) {
        console.log('🔍 [DaysService] removeAccommodationContentBlocks: Day not found', {
          userId,
          tripId,
          dayId
        });
        return { success: true, removedCount: 0, message: 'Day not found' };
      }

      // Get current content and ensure it's in structured format
      let currentContent = await this.getDayContent(userId, tripId, dayId);

      console.log('📊 [DaysService] removeAccommodationContentBlocks: Current content retrieved', {
        dayId,
        currentContentLength: currentContent.length,
        contentBlockTypes: currentContent.map(block => block.type)
      });

      // Find accommodation blocks that reference this accommodation
      // This includes both:
      // 1. Direct accommodation ID matches (new content blocks)
      // 2. Legacy accommodation blocks that were converted from strings (have fake IDs but real accommodation names)
      const accommodationBlocksToRemove = currentContent.filter(block => {
        if (block.type !== 'accommodation_reference') {
          return false;
        }

        // Direct accommodation ID match (for new content blocks)
        if (block.accommodationId === accommodationId) {
          return true;
        }

        // For legacy converted blocks, check if the accommodationId starts with 'legacy_acc_'
        // and the accommodationName matches what we expect for this accommodation
        if (block.accommodationId && block.accommodationId.startsWith('legacy_acc_')) {
          // We need to get the accommodation details to compare names
          // For now, we'll use a simple approach: if it's a legacy block, we'll remove it
          // This is safe for cleanup during date changes since we're rebuilding all content
          console.log(`🔍 [DaysService] removeAccommodationContentBlocks: Found legacy accommodation block`, {
            blockId: block.id,
            legacyAccommodationId: block.accommodationId,
            accommodationName: block.accommodationName,
            targetAccommodationId: accommodationId
          });
          return true; // Remove all legacy blocks during cleanup
        }

        return false;
      });

      if (accommodationBlocksToRemove.length === 0) {
        console.log('🔍 [DaysService] removeAccommodationContentBlocks: No accommodation content blocks found for this accommodation', {
          dayId,
          accommodationId
        });
        return { success: true, removedCount: 0, message: 'No accommodation content blocks found' };
      }

      console.log('🗑️ [DaysService] removeAccommodationContentBlocks: Found accommodation content blocks to remove', {
        dayId,
        accommodationId,
        blocksToRemove: accommodationBlocksToRemove.length,
        blockIds: accommodationBlocksToRemove.map(block => block.id)
      });

      // Filter out the accommodation blocks that were identified for removal
      // This includes both direct ID matches and legacy blocks
      const blocksToRemoveIds = new Set(accommodationBlocksToRemove.map(block => block.id));
      const updatedContent = currentContent.filter(
        block => !blocksToRemoveIds.has(block.id)
      );

      console.log('🔧 [DaysService] removeAccommodationContentBlocks: Content filtered', {
        dayId,
        originalContentLength: currentContent.length,
        updatedContentLength: updatedContent.length,
        removedCount: accommodationBlocksToRemove.length
      });

      // Validate the updated content array
      const contentValidation = validateDayContentSchema(updatedContent);
      if (!contentValidation.isValid) {
        console.error('❌ [DaysService] removeAccommodationContentBlocks: Day content validation failed', {
          dayId,
          contentLength: updatedContent.length,
          errors: contentValidation.errors
        });
        throw new Error(`Invalid day content after removal: ${contentValidation.errors.join(', ')}`);
      }

      console.log('✅ [DaysService] removeAccommodationContentBlocks: Day content validation passed');

      // Update the day
      await updateDay(userId, tripId, dayId, { content: updatedContent });

      console.log('💾 [DaysService] removeAccommodationContentBlocks: Day updated in database');

      // Publish event for each removed content block
      for (const removedBlock of accommodationBlocksToRemove) {
        eventBridgeService.publish(EVENT_TYPES.DAY_CONTENT_BLOCK_REMOVED, {
          userId,
          tripId,
          dayId,
          contentBlock: removedBlock,
          accommodationId,
          timestamp: new Date().toISOString(),
          eventId: `evt_${Date.now()}_${removedBlock.id}`
        });
      }

      console.log('📡 [DaysService] removeAccommodationContentBlocks: Events published', {
        eventType: EVENT_TYPES.DAY_CONTENT_BLOCK_REMOVED,
        dayId,
        accommodationId,
        removedCount: accommodationBlocksToRemove.length
      });

      const result = {
        success: true,
        removedCount: accommodationBlocksToRemove.length,
        removedBlocks: accommodationBlocksToRemove.map(block => ({
          id: block.id,
          displayText: block.displayText,
          context: block.context
        })),
        dayId
      };

      console.log('✅ [DaysService] removeAccommodationContentBlocks: Operation completed successfully', result);

      return result;
    } catch (error) {
      console.error('❌ [DaysService] removeAccommodationContentBlocks: Operation failed', {
        userId,
        tripId,
        dayId,
        accommodationId,
        error: error.message,
        stack: error.stack
      });
      throw error;
    }
  },

  /**
   * Initializes the days service
   * Subscribes to relevant events
   */
  init() {
    // Subscribe to document updated events
    eventBridgeService.subscribe(EVENT_TYPES.DOCUMENT_UPDATED, this.handleDocumentUpdated.bind(this));
    console.log(`DaysService subscribed to ${EVENT_TYPES.DOCUMENT_UPDATED} event.`);

    // Subscribe to document deleted events
    eventBridgeService.subscribe(EVENT_TYPES.DOCUMENT_DELETED, this.handleDocumentDeleted.bind(this));
    console.log(`DaysService subscribed to ${EVENT_TYPES.DOCUMENT_DELETED} event.`);

    // Subscribe to travel deleted events
    eventBridgeService.subscribe(EVENT_TYPES.TRAVEL_DELETED, this.handleTravelDeleted.bind(this));
    console.log(`DaysService subscribed to ${EVENT_TYPES.TRAVEL_DELETED} event.`);

    console.log('Days service initialized and all relevant event subscriptions are active.');
  },

  /**
   * Handles document updated events
   * Updates document reference blocks with new metadata
   * 
   * @param {Object} eventData - The document update event data
   */
  async handleDocumentUpdated(eventData) {
    try {
      const { userId, tripId, documentId, updateData } = eventData;
      const { linkRegistryConfigService } = await import(
        '@trip-planner-monorepo/shared/services/linkRegistryConfigService'
      );
      const config = await linkRegistryConfigService.getLinkRegistryConfig(userId, tripId);
      if (config.writeMode === 'registry_only') {
        return;
      }

      console.log('📄 [DaysService] handleDocumentUpdated: Processing document update', {
        userId,
        tripId,
        documentId,
        hasUpdateData: !!updateData
      });

      if (!userId || !tripId || !documentId) {
        console.warn('⚠️ [DaysService] handleDocumentUpdated: Missing required event data');
        return;
      }

      // Get all days for the trip
      const days = await this.fetchDays(userId, tripId);
      if (!days || days.length === 0) {
        console.log('📅 [DaysService] handleDocumentUpdated: No days found for trip');
        return;
      }

      // Process each day to update document references
      for (const day of days) {
        if (!day.contentBlocks || day.contentBlocks.length === 0) continue;

        // Check if this day has document references for the updated document
        const hasDocumentReference = day.contentBlocks.some(
          block => block.type === 'document_reference' && block.documentId === documentId
        );

        if (hasDocumentReference) {
          console.log('🔄 [DaysService] handleDocumentUpdated: Updating document references in day', {
            dayId: day.id,
            documentId
          });

          // Create updated document object for the utility function
          const updatedDocument = {
            id: documentId,
            ...updateData
          };

          // Update document reference blocks
          const updatedContentBlocks = updateDocumentReferenceBlocks(day.content, updatedDocument);

          // Update the day with new content blocks
          await updateDay(userId, tripId, day.id, {
            content: updatedContentBlocks
          });

          console.log('✅ [DaysService] handleDocumentUpdated: Successfully updated document references in day', {
            dayId: day.id,
            documentId
          });
        }
      }
    } catch (error) {
      console.error('❌ [DaysService] handleDocumentUpdated: Error updating document references', {
        error: error.message,
        stack: error.stack
      });
    }
  },

  /**
   * Handles document deleted events
   * Removes document reference blocks from days
   * 
   * @param {Object} eventData - The document deletion event data
   */
  async handleDocumentDeleted(eventData) {
    try {
      const { userId, tripId, documentId } = eventData;
      const { linkRegistryConfigService } = await import(
        '@trip-planner-monorepo/shared/services/linkRegistryConfigService'
      );
      const config = await linkRegistryConfigService.getLinkRegistryConfig(userId, tripId);
      if (config.writeMode === 'registry_only') {
        return;
      }

      console.log('🗑️ [DaysService] handleDocumentDeleted: Processing document deletion', {
        userId,
        tripId,
        documentId
      });

      if (!userId || !tripId || !documentId) {
        console.warn('⚠️ [DaysService] handleDocumentDeleted: Missing required event data');
        return;
      }

      // Get all days for the trip
      const days = await this.fetchDays(userId, tripId);
      if (!days || days.length === 0) {
        console.log('📅 [DaysService] handleDocumentDeleted: No days found for trip');
        return;
      }

      // Process each day to remove document references
      for (const day of days) {
        if (!day.contentBlocks || day.contentBlocks.length === 0) continue;

        // Check if this day has document references for the deleted document
        const hasDocumentReference = day.contentBlocks.some(
          block => block.type === 'document_reference' && block.documentId === documentId
        );

        if (hasDocumentReference) {
          console.log('🗑️ [DaysService] handleDocumentDeleted: Removing document references from day', {
            dayId: day.id,
            documentId
          });

          // Remove document reference blocks
          const updatedContentBlocks = removeDocumentReferenceBlocks(day.content, [documentId]);

          // Update the day with new content blocks
          await updateDay(userId, tripId, day.id, {
            content: updatedContentBlocks
          });

          console.log('✅ [DaysService] handleDocumentDeleted: Successfully removed document references from day', {
            dayId: day.id,
            documentId
          });
        }
      }
    } catch (error) {
      console.error('❌ [DaysService] handleDocumentDeleted: Error removing document references', {
        error: error.message,
        stack: error.stack
      });
    }
  },

  /**
   * Handles travel deleted events
   * Removes travel reference blocks from days
   * 
   * @param {Object} eventData - The travel deletion event data
   */
  async handleTravelDeleted(eventData) {
    try {
      const { userId, tripId, id: travelId, summary: travelSummary } = eventData;
      console.log('🗑️ [DaysService] handleTravelDeleted: Processing travel deletion', {
        userId,
        tripId,
        travelId,
        travelSummary
      });

      if (!userId || !tripId || !travelId) {
        console.warn('⚠️ [DaysService] handleTravelDeleted: Missing required event data');
        return;
      }

      // Get all days for the trip
      const days = await this.fetchDays(userId, tripId);
      if (!days || days.length === 0) {
        console.log('📅 [DaysService] handleTravelDeleted: No days found for trip');
        return;
      }

      let totalCleaned = 0;

      // Process each day to remove travel references
      for (const day of days) {
        // Get structured content for the day
        let currentContent;
        try {
          currentContent = await this.getDayContent(userId, tripId, day.id);
        } catch (err) {
          console.warn(`⚠️ [DaysService] handleTravelDeleted: Failed to get content for day ${day.id}`, err.message);
          continue;
        }

        if (!currentContent || currentContent.length === 0) continue;

        // Check if this day has travel references for the deleted travel
        const hasTravelReference = currentContent.some(
          block => block.type === 'travel_reference' && block.travelId === travelId
        );

        if (hasTravelReference) {
          console.log('🗑️ [DaysService] handleTravelDeleted: Removing travel references from day', {
            dayId: day.id,
            travelId
          });

          // Filter out travel reference blocks for this travel
          const updatedContent = currentContent.filter(
            block => !(block.type === 'travel_reference' && block.travelId === travelId)
          );

          const removedCount = currentContent.length - updatedContent.length;
          totalCleaned += removedCount;

          // Update the day with new content
          await updateDay(userId, tripId, day.id, {
            content: updatedContent
          });

          console.log('✅ [DaysService] handleTravelDeleted: Successfully removed travel references from day', {
            dayId: day.id,
            travelId,
            removedCount
          });
        }
      }

      console.log('✅ [DaysService] handleTravelDeleted: Cleanup completed', {
        tripId,
        travelId,
        totalCleaned
      });
    } catch (error) {
      console.error('❌ [DaysService] handleTravelDeleted: Error removing travel references', {
        error: error.message,
        stack: error.stack
      });
    }
  }
};

